import type { CSSProperties } from "react";
import type {
  Survey,
  SurveyAppearance,
  SurveyAppearanceCardStyle,
  SurveyAppearanceLogoAlignment,
  SurveyAppearanceTheme,
  SurveyAppearanceVerticalAlignment,
} from "@/types/survey";

/** Color used as a last-resort brand accent when neither the survey
 *  nor the organization defines one. Single source of truth — both the
 *  editor preview and the public page used to hardcode this. */
export const BRAND_COLOR_FALLBACK = "#6b46c1";

const DEFAULT_LIGHT_BG = "#f5f6fa";
const DEFAULT_LIGHT_BG_TO = "#ffffff";
const DEFAULT_DARK_BG = "#0f1115";
const DEFAULT_DARK_BG_TO = "#1c1f27";
export const DEFAULT_GLASS_CARD_OPACITY = 0.85;
export const MIN_GLASS_CARD_OPACITY = 0.25;
export const MAX_GLASS_CARD_OPACITY = 1;
/** Logo max-height in px. Default matches the historic `.logo` height in
 *  `SurveyBrand.module.css`; min/max keep the logo legible without
 *  letting it dominate or disappear. */
export const DEFAULT_LOGO_SIZE = 84;
export const MIN_LOGO_SIZE = 40;
export const MAX_LOGO_SIZE = 160;

interface BrandColorSource {
  color?: string | null;
}

interface OrgColorSource {
  orgColor?: string | null;
}

interface OrgLogoSource {
  orgLogoUrl?: string | null;
  name?: string | null;
}

interface SurveyLogoSource {
  logoUrl?: string | null;
}

export function getBrandColor(
  survey: BrandColorSource | null | undefined,
  organization: OrgColorSource | null | undefined,
): string {
  if (survey?.color) return survey.color;
  if (organization?.orgColor) return organization.orgColor;
  return BRAND_COLOR_FALLBACK;
}

export function getLogoUrl(
  survey: SurveyLogoSource | null | undefined,
  organization: OrgLogoSource | null | undefined,
): string | null {
  if (survey?.logoUrl) return survey.logoUrl;
  if (organization?.orgLogoUrl) return organization.orgLogoUrl;
  return null;
}

interface AppearanceSource {
  appearance?: SurveyAppearance | null;
}

/** Pull a normalized appearance object off a survey, defaulting to an
 *  empty record when none is set. */
export function getAppearance(
  survey: AppearanceSource | null | undefined,
): SurveyAppearance {
  return survey?.appearance ?? {};
}

export function getAppearanceTheme(
  appearance: SurveyAppearance,
): SurveyAppearanceTheme {
  return appearance.theme === "dark" ? "dark" : "light";
}

export function getCardStyle(
  appearance: SurveyAppearance,
): SurveyAppearanceCardStyle {
  return appearance.cardStyle ?? "elevated";
}

export function getLogoAlignment(
  appearance: SurveyAppearance,
): SurveyAppearanceLogoAlignment {
  return appearance.logoAlignment ?? "center";
}

export function getVerticalAlignment(
  appearance: SurveyAppearance,
): SurveyAppearanceVerticalAlignment {
  return appearance.verticalAlignment === "top" ? "top" : "center";
}

export function normalizeLogoSize(value: unknown): number {
  const numeric =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN;
  if (!Number.isFinite(numeric)) return DEFAULT_LOGO_SIZE;
  return Math.min(MAX_LOGO_SIZE, Math.max(MIN_LOGO_SIZE, numeric));
}

export function getLogoSize(appearance: SurveyAppearance): number {
  return normalizeLogoSize(appearance.logoSize);
}

export function normalizeGlassCardOpacity(value: unknown): number {
  const numeric =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN;
  if (!Number.isFinite(numeric)) return DEFAULT_GLASS_CARD_OPACITY;
  return Math.min(
    MAX_GLASS_CARD_OPACITY,
    Math.max(MIN_GLASS_CARD_OPACITY, numeric),
  );
}

export function getGlassCardOpacity(appearance: SurveyAppearance): number {
  return normalizeGlassCardOpacity(appearance.cardOpacity);
}

/** Inline style for the canvas / page surface that sits behind the
 *  survey card: solid color, gradient, or background image. Same logic
 *  the public page and editor canvas were each computing locally. */
export function getSurfaceBackgroundStyle(
  appearance: SurveyAppearance,
): CSSProperties {
  const bgType = appearance.backgroundType ?? "color";
  const isDark = getAppearanceTheme(appearance) === "dark";
  const bg =
    appearance.backgroundColor || (isDark ? DEFAULT_DARK_BG : DEFAULT_LIGHT_BG);
  const bgTo =
    appearance.backgroundColorTo ||
    (isDark ? DEFAULT_DARK_BG_TO : DEFAULT_LIGHT_BG_TO);
  if (bgType === "image" && appearance.backgroundImageUrl) {
    return {
      backgroundImage: `url(${appearance.backgroundImageUrl})`,
      backgroundColor: bg,
    };
  }
  if (bgType === "gradient") {
    return {
      backgroundImage: `linear-gradient(135deg, ${bg} 0%, ${bgTo} 100%)`,
    };
  }
  return { backgroundColor: bg };
}

/** Inline CSS variables to set on the surface element. Defaults for the
 *  rest live in `surveyTheme.module.css` so the editor and public page
 *  render identically. */
export function getSurfaceCssVars(
  brandColor: string,
  glassCardOpacity = DEFAULT_GLASS_CARD_OPACITY,
): CSSProperties {
  return {
    ["--sl-brand" as string]: brandColor,
    ["--sl-card-bg-opacity" as string]: `${Math.round(
      normalizeGlassCardOpacity(glassCardOpacity) * 100,
    )}%`,
  } as CSSProperties;
}

/** Convenience: combine background + brand var into one style. */
export function getSurfaceStyle(
  appearance: SurveyAppearance,
  brandColor: string,
): CSSProperties {
  return {
    ...getSurfaceCssVars(brandColor, getGlassCardOpacity(appearance)),
    ...getSurfaceBackgroundStyle(appearance),
  };
}

/** Single entry point an outer component can use to derive everything
 *  it needs to theme a survey surface. Consumers spread `style` onto
 *  the surface element and apply `dataTheme` via `data-theme=...`. */
export function getSurveyTheme(
  survey:
    | (BrandColorSource & SurveyLogoSource & AppearanceSource)
    | Survey
    | null
    | undefined,
  organization: (OrgColorSource & OrgLogoSource) | null | undefined,
): {
  appearance: SurveyAppearance;
  dataTheme: SurveyAppearanceTheme;
  cardStyle: SurveyAppearanceCardStyle;
  cardOpacity: number;
  brandColor: string;
  logoUrl: string | null;
  logoAlignment: SurveyAppearanceLogoAlignment;
  logoSize: number;
  verticalAlignment: SurveyAppearanceVerticalAlignment;
  style: CSSProperties;
} {
  const appearance = getAppearance(survey ?? null);
  const dataTheme = getAppearanceTheme(appearance);
  const cardStyle = getCardStyle(appearance);
  const cardOpacity = getGlassCardOpacity(appearance);
  const brandColor = getBrandColor(survey ?? null, organization ?? null);
  const logoUrl = getLogoUrl(survey ?? null, organization ?? null);
  const logoAlignment = getLogoAlignment(appearance);
  const logoSize = getLogoSize(appearance);
  const verticalAlignment = getVerticalAlignment(appearance);
  return {
    appearance,
    dataTheme,
    cardStyle,
    cardOpacity,
    brandColor,
    logoUrl,
    logoAlignment,
    logoSize,
    verticalAlignment,
    style: getSurfaceStyle(appearance, brandColor),
  };
}
